/**
 * Patient 360° detail drawer.
 *
 * Fixed header zone (always visible while scrolling within the panel): core metadata,
 * status pills, engagement flags, alerts and the KPI strip.
 *
 * Tabbed body: Appointments / Sessions / Billing & Receipts. Each tab lazy-fetches its
 * historical rows — RTK Query is skipped until the tab is activated. The first 25 rows
 * come straight from the `/patients/{id}/overview` payload so the active tab paints
 * without a second round-trip; "Load more" pulls subsequent pages from the dedicated
 * /api/{appointments,sessions,invoices} endpoints.
 */
import React, { useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import {
  X, AlertTriangle, Phone, Calendar, Stethoscope, Pencil, Trash2,
  Receipt, ListChecks, ChevronDown, ChevronRight,
} from 'lucide-react';
import { toast } from 'react-toastify';
import {
  useGetPatientOverviewQuery,
  useUpdatePatientMutation,
  useUpdatePatientEngagementMutation,
} from '../../api/patientsApi';
import DeletePatientModal from './DeletePatientModal';
import SessionLedgerModal from './SessionLedgerModal';
import { useListAppointmentsQuery } from '../../api/appointmentsApi';
import { useListSessionsQuery } from '../../api/sessionsApi';
import { useListInvoicesQuery, useGetInvoiceQuery } from '../../api/billingApi';
import { useListDoctorsQuery } from '../../api/usersApi';
import { RENEWAL_PITCH_OPTIONS } from './RenewalPitchModal';
import { selectHasRole } from '../auth/authSlice';
import { SESSION_STATUS, APPOINTMENT_STATUS } from '../../utils/status';
import { isImported, cleanNotes } from '../../utils/imported';
import { doctorDisplayName } from '../appointments/doctorColors';
import { procedureLabel } from '../appointments/appointmentStatus';

const PAGE_SIZE = 25;

const fmtINR = (v) => new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', maximumFractionDigits: 0 }).format(v ?? 0);
// Year is mandatory on every timestamp in this view — a bare day/month reads ambiguously once
// a patient's history spans more than one year (appointments, sessions, billing, receipts).
const fmtDate = (d) => (d ? new Date(d).toLocaleDateString('en-IN', { timeZone: 'Asia/Kolkata', day: '2-digit', month: 'short', year: 'numeric' }) : '—');
const fmtDateTime = (d) => (d ? new Date(d).toLocaleString('en-IN', { timeZone: 'Asia/Kolkata', day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : '—');

function Stat({ label, value, accent }) {
  return (
    <div className="card" style={{ padding: '12px 14px' }}>
      <div style={{ fontSize: '0.68rem', fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.06em' }}>{label}</div>
      <div style={{ fontSize: '1.2rem', fontWeight: 800, marginTop: 4, color: accent || 'var(--text-primary)' }}>{value}</div>
    </div>
  );
}

function Section({ title, icon: Icon, children, action }) {
  return (
    <div style={{ marginTop: 14 }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10, marginBottom: 8 }}>
        <h3 style={{ fontSize: '0.82rem', fontWeight: 700, color: 'var(--text-secondary)', display: 'flex', alignItems: 'center', gap: 6, margin: 0 }}>
          {Icon && <Icon size={15} />} {title}
        </h3>
        {action}
      </div>
      {children}
    </div>
  );
}

const TABS = [
  { id: 'appointments', label: 'Appointment History', icon: Calendar },
  { id: 'sessions',     label: 'Session Log',         icon: ListChecks },
  { id: 'billing',      label: 'Billing & Receipts',  icon: Receipt },
];

export default function PatientDetailDrawer({ patientId, onClose }) {
  const { data: o, isLoading } = useGetPatientOverviewQuery(patientId, { skip: !patientId });
  const isAdmin = useSelector(selectHasRole('Admin'));
  const [editOpen, setEditOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [activeTab, setActiveTab] = useState('appointments');

  if (!patientId) return null;
  const p = o?.patient;

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal patient-detail-modal" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2>{isLoading ? 'Loading…' : (p?.fullName ?? 'Patient')}</h2>
          <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
            {p && <button className="btn btn-outline btn-sm" onClick={() => setEditOpen(true)}><Pencil size={13} /> Edit</button>}
            {p && isAdmin && (
              <button
                className="btn btn-outline btn-sm"
                style={{ color: 'var(--red-700)', borderColor: 'var(--red-300, var(--red-700))' }}
                onClick={() => setDeleteOpen(true)}
                title="Delete this patient and everything attached to them"
              >
                <Trash2 size={13} /> Delete
              </button>
            )}
            <button className="modal-close" onClick={onClose}><X size={18} /></button>
          </div>
        </div>
        {editOpen && p && <PatientEditModal patient={p} onClose={() => setEditOpen(false)} />}
        {deleteOpen && p && (
          <DeletePatientModal
            patientId={p.id}
            patientName={p.fullName}
            patientCode={p.patientCode}
            onClose={() => setDeleteOpen(false)}
            onDeleted={() => { setDeleteOpen(false); onClose(); }}
          />
        )}
        <div className="modal-body">
          {o && (
            <>
              {/* ── HEADER ZONE (always visible) ──────────────────────────── */}
              <ProfileHeader overview={o} />
              <SessionSummary overview={o} patientId={patientId} />
              <EngagementSection patient={p} />
              <KpiStrip overview={o} />
              <ProceduresPurchased overview={o} />
              {o.treatmentPlans?.length > 0 && <TreatmentPlansSection plans={o.treatmentPlans} />}
              {o.sessionsByDoctor?.length > 0 && <SessionsByDoctorSection rows={o.sessionsByDoctor} />}

              {/* ── TABBED HISTORY ────────────────────────────────────────── */}
              <div role="tablist" aria-label="Patient history" className="pd-tabs">
                {TABS.map((t) => {
                  const Icon = t.icon;
                  const isActive = activeTab === t.id;
                  return (
                    <button
                      key={t.id}
                      role="tab"
                      aria-selected={isActive}
                      onClick={() => setActiveTab(t.id)}
                      className={`pd-tab ${isActive ? 'pd-tab--active' : ''}`}
                    >
                      <Icon size={14} /> {t.label}
                    </button>
                  );
                })}
              </div>

              <div role="tabpanel" style={{ marginTop: 12 }}>
                {activeTab === 'appointments' && (
                  <AppointmentsTab
                    patientId={patientId}
                    firstPage={o.recentAppointments ?? []}
                    total={o.totalAppointments ?? (o.recentAppointments?.length ?? 0)}
                  />
                )}
                {activeTab === 'sessions' && (
                  <SessionsTab
                    patientId={patientId}
                    firstPage={o.recentSessions ?? []}
                    total={o.totalSessionsCount ?? (o.recentSessions?.length ?? 0)}
                  />
                )}
                {activeTab === 'billing' && (
                  <BillingTab
                    patientId={patientId}
                    firstInvoices={o.invoices ?? []}
                    totalInvoices={o.totalInvoicesCount ?? (o.invoices?.length ?? 0)}
                    payments={o.payments ?? []}
                  />
                )}
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

// ─── Header zone ────────────────────────────────────────────────────────────
function ProfileHeader({ overview: o }) {
  const p = o.patient;
  return (
    <>
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 12, alignItems: 'center' }}>
        <span className="td-mono" style={{ fontWeight: 700 }}>{p.patientCode}</span>
        <span className={`badge ${o.isActive ? 'b-green' : 'b-gray'}`}>{o.isActive ? 'Active' : 'Inactive'}</span>
        {o.assignedDoctorName && <span className="badge b-blue">Dr. {o.assignedDoctorName.replace(/^Dr\.\s*/i, '')}</span>}
      </div>

      {o.alerts?.length > 0 && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginBottom: 12 }}>
          {o.alerts.map((a, i) => (
            <div key={i} style={{
              display: 'flex', alignItems: 'center', gap: 8, padding: '8px 12px',
              background: 'var(--amber-100)', color: '#92400e', borderRadius: 'var(--r-md)', fontSize: '0.82rem',
            }}>
              <AlertTriangle size={15} style={{ flexShrink: 0 }} /> {a}
            </div>
          ))}
        </div>
      )}

      <div className="card card-body" style={{ marginBottom: 4 }}>
        <div className="info-row"><div className="ir-label">Phone</div><div className="ir-value"><Phone size={14} /> {p.phoneNumber}</div></div>
        {p.city && <div className="info-row"><div className="ir-label">City</div><div className="ir-value">{p.city}</div></div>}
        <div className="info-row"><div className="ir-label">Patient since</div><div className="ir-value">{fmtDate(p.createdAt)}</div></div>
      </div>
    </>
  );
}

// Prominent "X / Y Sessions Remaining" banner — maps directly to the live ledger values on the
// overview payload (purchased = billed procedure quantities, used = completed appointments).
function SessionSummary({ overview: o, patientId }) {
  const [showLedger, setShowLedger] = useState(false);
  const total = o.totalSessions ?? 0;
  const used = o.sessionsUsed ?? 0;
  // NOT clamped: a patient can be treated past their balance, and that has to stay visible.
  const remaining = o.remainingSessions ?? (total - used);
  // Only the BAR is clamped — the number above it always tells the truth.
  const pctUsed = total > 0 ? Math.min(100, Math.round((used / total) * 100)) : 0;
  const overUsed = remaining < 0;
  const lowOrNone = remaining <= 0;
  return (
    <Section title="Sessions" icon={ListChecks}>
      <button
        type="button"
        className="card card-body"
        onClick={() => setShowLedger(true)}
        title="View the per-procedure breakdown"
        style={{
          padding: '14px 16px', width: '100%', textAlign: 'left',
          cursor: 'pointer', border: '1px solid var(--border)', background: 'var(--surface, #fff)',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'baseline', gap: 8, flexWrap: 'wrap' }}>
          <span style={{ fontSize: '1.6rem', fontWeight: 800, color: lowOrNone ? 'var(--red-700)' : 'var(--green-700)' }}>
            {total} / {used}
          </span>
          <span style={{ fontWeight: 700, color: 'var(--text-secondary)' }}>Total Sessions / Used</span>
          <span
            style={{
              marginLeft: 'auto', fontSize: '0.8rem',
              color: overUsed ? 'var(--red-700)' : 'var(--text-muted)',
              fontWeight: overUsed ? 700 : 400,
            }}
          >
            {overUsed ? `${Math.abs(remaining)} over` : `${remaining} remaining`}
          </span>
        </div>
        <div style={{ height: 8, borderRadius: 999, background: 'var(--gray-100)', overflow: 'hidden', marginTop: 10 }}>
          <div style={{ width: `${pctUsed}%`, height: '100%', background: lowOrNone ? 'var(--red-500)' : 'var(--green-500)' }} />
        </div>
        <div className="td-muted" style={{ fontSize: '0.72rem', marginTop: 8 }}>
          Click for the per-procedure breakdown
        </div>
      </button>
      {showLedger && <SessionLedgerModal patientId={patientId} onClose={() => setShowLedger(false)} />}
    </Section>
  );
}

function KpiStrip({ overview: o }) {
  return (
    <Section title="Billing">
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 8 }}>
        <Stat label="Total invoiced" value={fmtINR(o.totalBilled)} />
        <Stat label="Total paid" value={fmtINR(o.totalPaid)} accent="var(--green-700)" />
        <Stat label="Outstanding" value={fmtINR(o.outstanding)} accent={o.outstanding > 0 ? 'var(--red-700)' : undefined} />
        <Stat label="Advance balance" value={fmtINR(o.advanceBalance)} accent="var(--blue-800)" />
        <Stat label="Sessions by advance" value={o.sessionsCoveredByAdvance} />
      </div>
    </Section>
  );
}

// Concise "what did they buy" list — one row per invoice showing the procedure(s), the quantity
// purchased, the invoiced amount and the outstanding balance, with a patient-level totals footer.
function ProceduresPurchased({ overview: o }) {
  const invoices = o.invoices ?? [];
  if (!invoices.length) return null;
  return (
    <Section title="Procedures purchased" icon={Receipt}>
      <div className="tbl-wrap">
        <table>
          <thead>
            <tr>
              <th>Treatment / procedure</th>
              <th style={{ textAlign: 'right' }}>Qty</th>
              <th style={{ textAlign: 'right' }}>Invoiced</th>
              <th style={{ textAlign: 'right' }}>Outstanding</th>
            </tr>
          </thead>
          <tbody>
            {invoices.map((i) => (
              <tr key={i.id}>
                <td>{i.treatmentSummary || '—'}</td>
                <td style={{ textAlign: 'right' }}>{i.totalSessions ?? 0}</td>
                <td style={{ textAlign: 'right' }}>{fmtINR(i.totalAmount)}</td>
                <td className="td-bold"
                  style={{ textAlign: 'right', color: i.balanceDue > 0 ? 'var(--red-700)' : 'var(--green-700)' }}>
                  {fmtINR(i.balanceDue)}
                </td>
              </tr>
            ))}
          </tbody>
          <tfoot>
            <tr>
              <td className="td-bold">Total</td>
              <td />
              <td className="td-bold" style={{ textAlign: 'right' }}>{fmtINR(o.totalBilled)}</td>
              <td className="td-bold" style={{ textAlign: 'right', color: o.outstanding > 0 ? 'var(--red-700)' : 'var(--green-700)' }}>
                {fmtINR(o.outstanding)}
              </td>
            </tr>
          </tfoot>
        </table>
      </div>
    </Section>
  );
}

function TreatmentPlansSection({ plans }) {
  return (
    <Section title="Treatment plans" icon={Stethoscope}>
      {plans.map((t) => {
        const remaining = Math.max(0, t.recommendedSessions - t.completedSessions);
        return (
          <div key={t.id} className="card card-body" style={{ marginBottom: 8 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8, flexWrap: 'wrap' }}>
              <strong>{t.diagnosis || 'Treatment plan'}</strong>
              <span className="badge b-gray">{t.status}</span>
            </div>
            <div className="td-muted" style={{ fontSize: '0.8rem', marginTop: 4 }}>
              {t.completedSessions}/{t.recommendedSessions} sessions done · {remaining} remaining
              {t.intervalDays ? ` · every ${t.intervalDays} days` : ''}
              {t.assignedTherapistName ? ` · ${doctorDisplayName(t.assignedTherapistName)}` : ''}
            </div>
            <div className="td-muted" style={{ fontSize: '0.8rem', marginTop: 2 }}>Plan cost: {fmtINR(t.totalCost)}</div>
          </div>
        );
      })}
    </Section>
  );
}

function SessionsByDoctorSection({ rows }) {
  return (
    <Section title="Sessions by doctor">
      {rows.map((d) => (
        <div key={d.doctorId ?? 'none'} className="info-row">
          <div className="ir-label" style={{ minWidth: 'auto', flex: 1 }}>{d.doctorName ? doctorDisplayName(d.doctorName) : 'Unassigned'}</div>
          <div className="ir-value">{d.count} session{d.count === 1 ? '' : 's'}</div>
        </div>
      ))}
    </Section>
  );
}

// ─── Engagement flags ───────────────────────────────────────────────────────
function EngagementSection({ patient }) {
  const { data: doctors = [] } = useListDoctorsQuery();
  const [save, { isLoading }] = useUpdatePatientEngagementMutation();
  const onChange = async (patch) => {
    try { await save({ id: patient.id, ...patch }).unwrap(); }
    catch (e) { toast.error(e?.data?.error?.message || 'Could not update'); }
  };
  return (
    <Section title="Engagement & renewal">
      <div className="card card-body" style={{ display: 'grid', gap: 10 }}>
        <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: '0.85rem', cursor: 'pointer' }}>
          <input type="checkbox" checked={!!patient.googleReviewDone} disabled={isLoading}
            onChange={(e) => onChange({ googleReviewDone: e.target.checked })} />
          <span>Google Review Done</span>
        </label>
        <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: '0.85rem', cursor: 'pointer' }}>
          <input type="checkbox" checked={!!patient.feedbackFormCompleted} disabled={isLoading}
            onChange={(e) => onChange({ feedbackFormCompleted: e.target.checked })} />
          <span>Feedback Form Completed</span>
        </label>
        <div className="form-row">
          <div className="form-group" style={{ margin: 0 }}>
            <label className="form-label">Renewal Pitched</label>
            <select className="form-control" value={patient.renewalPitched || 'NotYet'} disabled={isLoading}
              onChange={(e) => onChange({ renewalPitched: e.target.value })}>
              <option value="NotYet">Not Yet</option>
              {RENEWAL_PITCH_OPTIONS.map((opt) => <option key={opt.value} value={opt.value}>{opt.label}</option>)}
            </select>
          </div>
          <div className="form-group" style={{ margin: 0 }}>
            <label className="form-label">Renewal Pitched By</label>
            <select className="form-control" value={patient.renewalPitchedById || ''} disabled={isLoading}
              onChange={(e) => onChange({ renewalPitchedById: e.target.value || null })}>
              <option value="">— Select doctor —</option>
              {doctors.map((d) => <option key={d.id} value={d.id}>{doctorDisplayName(d.fullName)}</option>)}
            </select>
          </div>
        </div>
      </div>
    </Section>
  );
}

// ─── Tabs: shared "load more" logic ────────────────────────────────────────
/**
 * Each tab paints from `firstPage` (already in the overview payload) and grows by fetching
 * page 2+ from the dedicated list endpoint when the user clicks "Load more". RTK Query is
 * skipped on the initial render so we save one round-trip in the common case.
 */
function useExtraPages(loadFn, baseFilter, enabled) {
  const [page, setPage] = useState(1);
  const { data, isFetching } = loadFn(
    { ...baseFilter, page: page + 1, pageSize: PAGE_SIZE },
    { skip: !enabled || page < 1 || page === 1 },
  );
  const [accum, setAccum] = useState([]);
  React.useEffect(() => {
    if (page > 1 && data?.items?.length) {
      setAccum((prev) => {
        const seen = new Set(prev.map((x) => x.id));
        return [...prev, ...data.items.filter((x) => !seen.has(x.id))];
      });
    }
  }, [data, page]);
  return { extra: accum, isFetching, page, loadMore: () => setPage((p) => p + 1) };
}

// ─── Tab: Appointments ────────────────────────────────────────────────────
function AppointmentsTab({ patientId, firstPage, total }) {
  const { extra, isFetching, page, loadMore } = useExtraPages(
    useListAppointmentsQuery,
    { patientId, sortBy: 'scheduledAt', sortDesc: true },
    true,
  );
  const rows = useMemo(() => [...firstPage, ...extra], [firstPage, extra]);
  const hasMore = rows.length < total;

  if (!rows.length) {
    return <div className="td-muted" style={{ fontSize: '0.83rem' }}>No appointments recorded.</div>;
  }
  return (
    <>
      <div className="tbl-wrap">
        <table>
          <thead><tr><th>Date</th><th>Status</th><th>Doctor</th><th>Procedure</th><th>Notes</th></tr></thead>
          <tbody>
            {rows.map((a) => {
              const st = APPOINTMENT_STATUS[a.status] || { label: a.status, color: 'b-gray' };
              const note = cleanNotes(a.notes);
              const procedure = procedureLabel(a);
              return (
                <tr key={a.id}>
                  <td className="td-muted">{fmtDateTime(a.scheduledAt)}</td>
                  <td><span className={`badge ${st.color}`}>{st.label}</span></td>
                  <td>{a.assignedUserName ? doctorDisplayName(a.assignedUserName) : '—'}</td>
                  <td className={procedure ? undefined : 'td-muted'}>{procedure || '—'}</td>
                  <td className="td-muted">{note || (isImported(a.notes) ? 'Imported' : '—')}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      <LoadMoreFooter shown={rows.length} total={total} hasMore={hasMore} isFetching={isFetching} onClick={loadMore} />
    </>
  );
}

// ─── Tab: Sessions ────────────────────────────────────────────────────────
function SessionsTab({ patientId, firstPage, total }) {
  const { extra, isFetching, loadMore } = useExtraPages(
    useListSessionsQuery,
    { patientId, sortBy: 'scheduledAt', sortDesc: true },
    true,
  );
  const rows = useMemo(() => [...firstPage, ...extra], [firstPage, extra]);
  const hasMore = rows.length < total;

  if (!rows.length) {
    return <div className="td-muted" style={{ fontSize: '0.83rem' }}>No sessions recorded.</div>;
  }
  return (
    <>
      <div className="tbl-wrap">
        <table>
          <thead><tr><th>When</th><th>Procedure</th><th>Therapist</th><th>Package</th><th>Notes</th><th>Status</th></tr></thead>
          <tbody>
            {rows.map((s) => {
              const st = SESSION_STATUS[s.status] || { label: s.status, color: 'b-gray' };
              return (
                <tr key={s.id}>
                  <td className="td-muted">{fmtDateTime(s.scheduledAt)}</td>
                  <td>{s.treatmentTypeName ?? 'Session'}</td>
                  <td>{s.therapistName ? doctorDisplayName(s.therapistName) : '—'}</td>
                  <td className="td-muted">{s.sessionPackageName || s.packageName || '—'}</td>
                  <td className="td-muted">{cleanNotes(s.notes) || '—'}</td>
                  <td><span className={`badge ${st.color}`}>{st.label}</span></td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      <LoadMoreFooter shown={rows.length} total={total} hasMore={hasMore} isFetching={isFetching} onClick={loadMore} />
    </>
  );
}

// ─── Tab: Billing ─────────────────────────────────────────────────────────
function BillingTab({ patientId, firstInvoices, totalInvoices, payments }) {
  const { extra, isFetching, loadMore } = useExtraPages(
    useListInvoicesQuery,
    { patientId, sortBy: 'issuedAt', sortDesc: true },
    true,
  );
  const invoices = useMemo(() => [...firstInvoices, ...extra], [firstInvoices, extra]);
  const hasMore = invoices.length < totalInvoices;

  return (
    <>
      <Section title="Invoices">
        {invoices.length ? (
          <>
            <div className="tbl-wrap">
              <table>
                <thead><tr><th /><th>Invoice #</th><th>Issued</th><th>Total</th><th>Paid</th><th>Balance</th><th>Status</th></tr></thead>
                <tbody>
                  {invoices.map((i) => <InvoiceRow key={i.id} invoice={i} />)}
                </tbody>
              </table>
            </div>
            <LoadMoreFooter shown={invoices.length} total={totalInvoices} hasMore={hasMore} isFetching={isFetching} onClick={loadMore} />
          </>
        ) : <div className="td-muted" style={{ fontSize: '0.83rem' }}>No invoices.</div>}
      </Section>

      <Section title="Receipts / payments" icon={Receipt}>
        {payments.length ? (
          <div className="tbl-wrap">
            <table>
              <thead><tr><th>Paid at</th><th>Amount</th><th>Method</th><th>Reference</th><th>Notes</th></tr></thead>
              <tbody>
                {payments.map((pay) => (
                  <tr key={pay.id}>
                    <td className="td-muted">{fmtDateTime(pay.paidAt)}</td>
                    <td className="td-bold">{fmtINR(pay.amount)}</td>
                    <td>{pay.method ?? '—'}</td>
                    <td className="td-mono">{pay.transactionReference ?? '—'}</td>
                    <td className="td-muted">{pay.notes ?? '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : <div className="td-muted" style={{ fontSize: '0.83rem' }}>No payments recorded.</div>}
      </Section>
    </>
  );
}

// Invoice row + itemized breakdown — line items are fetched on demand (via the single-invoice
// endpoint, which is the only one that returns them) rather than bloated into the list payload.
function InvoiceRow({ invoice: i }) {
  const [expanded, setExpanded] = useState(false);
  return (
    <>
      <tr onClick={() => setExpanded((v) => !v)} style={{ cursor: 'pointer' }}>
        <td style={{ width: 24, color: 'var(--text-muted)' }}>{expanded ? <ChevronDown size={14} /> : <ChevronRight size={14} />}</td>
        <td className="td-mono">{i.invoiceNumber}</td>
        <td className="td-muted">{fmtDate(i.issuedAt)}</td>
        <td>{fmtINR(i.totalAmount)}</td>
        <td className="td-muted">{fmtINR(i.amountPaid)}</td>
        <td className="td-bold" style={{ color: i.balanceDue > 0 ? 'var(--red-700)' : 'var(--green-700)' }}>{fmtINR(i.balanceDue)}</td>
        <td><span className="badge b-gray">{i.status}</span></td>
      </tr>
      {expanded && (
        <tr>
          <td colSpan={7} style={{ background: 'var(--gray-50)', padding: '10px 14px' }}>
            <InvoiceItemsPanel invoiceId={i.id} />
          </td>
        </tr>
      )}
    </>
  );
}

function InvoiceItemsPanel({ invoiceId }) {
  const { data: inv, isFetching } = useGetInvoiceQuery(invoiceId);
  if (isFetching && !inv) return <div className="td-muted" style={{ fontSize: '0.8rem' }}>Loading procedures…</div>;
  const items = inv?.items ?? [];
  if (!items.length) return <div className="td-muted" style={{ fontSize: '0.8rem' }}>No itemized procedures on this invoice.</div>;
  return (
    <table style={{ width: '100%' }}>
      <thead>
        <tr>
          <th style={{ fontSize: '0.72rem' }}>Procedure</th>
          <th style={{ fontSize: '0.72rem', textAlign: 'right' }}>Qty</th>
          <th style={{ fontSize: '0.72rem', textAlign: 'right' }}>Rate</th>
          <th style={{ fontSize: '0.72rem', textAlign: 'right' }}>Line total</th>
        </tr>
      </thead>
      <tbody>
        {items.map((it) => (
          <tr key={it.id}>
            <td style={{ fontSize: '0.82rem' }}>{it.description}</td>
            <td style={{ fontSize: '0.82rem', textAlign: 'right' }}>{it.quantity}</td>
            <td style={{ fontSize: '0.82rem', textAlign: 'right' }} className="td-muted">{fmtINR(it.unitPrice)}</td>
            <td style={{ fontSize: '0.82rem', textAlign: 'right' }} className="td-bold">{fmtINR(it.lineTotal)}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

function LoadMoreFooter({ shown, total, hasMore, isFetching, onClick }) {
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 8 }}>
      <div className="td-muted" style={{ fontSize: '0.76rem' }}>Showing {shown} of {total}.</div>
      {hasMore && (
        <button className="btn btn-outline btn-sm" onClick={onClick} disabled={isFetching}>
          <ChevronDown size={13} /> {isFetching ? 'Loading…' : 'Load more'}
        </button>
      )}
    </div>
  );
}

// ─── Edit modal (unchanged) ────────────────────────────────────────────────
function PatientEditModal({ patient, onClose }) {
  const [form, setForm] = useState({
    fullName: patient.fullName ?? '',
    phoneNumber: patient.phoneNumber ?? '',
    city: patient.city ?? '',
    address: patient.address ?? '',
    gender: patient.gender ?? 'Unspecified',
    dateOfBirth: patient.dateOfBirth ? patient.dateOfBirth.slice(0, 10) : '',
  });
  const [save, { isLoading }] = useUpdatePatientMutation();
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.fullName.trim() || !form.phoneNumber.trim()) { toast.error('Name and phone are required.'); return; }
    try {
      await save({
        id: patient.id,
        fullName: form.fullName.trim(),
        phoneNumber: form.phoneNumber.trim(),
        city: form.city?.trim() || null,
        address: form.address?.trim() || null,
        gender: form.gender,
        dateOfBirth: form.dateOfBirth ? new Date(form.dateOfBirth).toISOString() : null,
        assignedDoctorId: patient.assignedDoctorId ?? null,
      }).unwrap();
      toast.success('Patient updated');
      onClose();
    } catch (err) {
      toast.error(err?.data?.error?.message || 'Could not update patient.');
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <form className="modal" style={{ maxWidth: 440 }} onClick={(e) => e.stopPropagation()} onSubmit={submit}>
        <div className="modal-header"><h2>Edit patient</h2><button type="button" className="modal-close" onClick={onClose}><X size={18} /></button></div>
        <div className="modal-body">
          <div className="form-group"><label className="form-label">Full name *</label>
            <input className="form-control" value={form.fullName} onChange={(e) => set('fullName', e.target.value)} /></div>
          <div className="form-row">
            <div className="form-group"><label className="form-label">Phone *</label>
              <input className="form-control" value={form.phoneNumber} onChange={(e) => set('phoneNumber', e.target.value)} /></div>
            <div className="form-group"><label className="form-label">City</label>
              <input className="form-control" value={form.city} onChange={(e) => set('city', e.target.value)} /></div>
          </div>
          <div className="form-row">
            <div className="form-group"><label className="form-label">Gender</label>
              <select className="form-control" value={form.gender} onChange={(e) => set('gender', e.target.value)}>
                <option value="Unspecified">Unspecified</option>
                <option value="Male">Male</option>
                <option value="Female">Female</option>
                <option value="Other">Other</option>
              </select></div>
            <div className="form-group"><label className="form-label">Date of birth</label>
              <input className="form-control" type="date" value={form.dateOfBirth} onChange={(e) => set('dateOfBirth', e.target.value)} /></div>
          </div>
          <div className="form-group"><label className="form-label">Address</label>
            <textarea className="form-control" rows={2} value={form.address} onChange={(e) => set('address', e.target.value)} /></div>
        </div>
        <div className="modal-footer">
          <button type="button" className="btn btn-outline" onClick={onClose}>Cancel</button>
          <button type="submit" className="btn btn-primary" disabled={isLoading}>{isLoading ? 'Saving…' : 'Save changes'}</button>
        </div>
      </form>
    </div>
  );
}
